import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Textarea } from 'rizzui';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import useCategories from '../../../../../hooks/api-calls/useCategories';
import useUnitsMeasure from '../../../../../hooks/api-calls/useUntisMeasure';
import useFetch from '../../../../../hooks/useFetch';
import FormGroup from '../../../../../layout/shared/form-group';
import { ProductService } from '../../../../../services/product/productService';
import { ProductApiResponse } from '../../../../../type/entities/product-type';

export default function ProductInfo() {
    // HOOKS
    const { id = '' } = useParams<{ id: string }>();
    const service = new ProductService();
    const { getCategoriesList } = useCategories();
    const { getUnitsMeasureList } = useUnitsMeasure();

    // DATA FETCHING
    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LOS DATOS DEL PRODUCTO POR SU ID.
     * @EN FETCHES PRODUCT DATA BY ID.
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getProductById(id);
        return response.getResponseData() as ProductApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    // RENDER
    return (
        <div className="@container">
            <div className="mx-auto w-full max-w-10xl">
                <FormGroup
                    title="Información del Producto"
                    description="Información básica del producto"
                    className="pt-7 @2xl:pt-9 @3xl:grid-cols-12 @3xl:pt-11"
                >
                    <div className="@3xl:col-span-2">
                        <div className="grid gap-4 @lg:grid-cols-2 @2xl:gap-6 @3xl:grid-cols-2">
                            <Input
                                label="Nombre"
                                placeholder="Nombre del producto"
                                value={data?.name || ''}
                                readOnly
                                className="font-medium col-span-2 md:col-span-1"
                            />
                            <CustomSelect
                                id="category"
                                label="Categoría"
                                value={{
                                    value: data?.category?.id || '',
                                    label: data?.category?.name || '',
                                    color: data?.category?.color || '',
                                }}
                                options={getCategoriesList()}
                                containerClassName='col-span-2 md:col-span-1 pointer-events-none'
                                customOption={(option: any) => (
                                    <div>
                                        <div className='flex items-center'>
                                            {option.color && <div className='w-4 h-4 rounded border border-white mr-2' style={{ backgroundColor: option.color }}></div>}
                                            <span>{option.label}</span>
                                        </div>
                                    </div>
                                )}
                            />
                            <CustomSelect
                                id="unit_measure"
                                label="Unidad de Medida"
                                value={{
                                    value: data?.unitMeasure?.id || '',
                                    label: data?.unitMeasure?.name || ''
                                }}
                                options={getUnitsMeasureList()}
                                containerClassName='col-span-2 md:col-span-1 pointer-events-none'
                                isDisabled
                            />
                            <Input
                                label="Precio de Compra"
                                placeholder="0.00"
                                value={data?.purchasePrice ? `${data.purchasePrice} €` : ''}
                                readOnly
                                className="col-span-2 md:col-span-1 font-medium"
                            />
                            <Input
                                label="Stock Actual"
                                placeholder="0"
                                value={data?.currentStock?.toString() || '0'}
                                readOnly
                                className="col-span-2 md:col-span-1 font-medium"
                            />
                            <Input
                                label="Stock Mínimo"
                                placeholder="0"
                                value={data?.minimumStock?.toString() || '0'}
                                readOnly
                                className="col-span-2 md:col-span-1 font-medium"
                            />
                            <Textarea
                                label="Descripción"
                                placeholder="Descripción del producto"
                                value={data?.description || ''}
                                readOnly
                                className="col-span-2 font-medium"
                                rows={4}
                            />
                        </div>
                    </div>
                </FormGroup>

                <FormGroup
                    title="Información de la Empresa"
                    description="Datos de la organización"
                    className="pt-7 @2xl:pt-9 @3xl:grid-cols-12 @3xl:pt-11"
                >
                    <div className="@3xl:col-span-2">
                        <div className="grid gap-4 @lg:grid-cols-1 @2xl:gap-6 @3xl:grid-cols-1">
                            <Input
                                label="Empresa"
                                placeholder="Nombre de la empresa"
                                value={data?.company?.name || ''}
                                readOnly
                                className="font-medium"
                            />
                        </div>
                    </div>
                </FormGroup>

                <FormGroup
                    title="Fechas"
                    description="Información de fechas del producto"
                    className="pt-7 @2xl:pt-9 @3xl:grid-cols-12 @3xl:pt-11"
                >
                    <div className="@3xl:col-span-2">
                        <div className="grid gap-4 @lg:grid-cols-2 @2xl:gap-6 @3xl:grid-cols-2">
                            <Input
                                label="Fecha de Creación"
                                value={data?.createdAt?.date ? new Date(data.createdAt?.date).toLocaleDateString('es-ES') : ''}
                                readOnly
                                className="font-medium"
                            />
                            <Input
                                label="Última Actualización"
                                value={data?.updatedAt?.date ? new Date(data.updatedAt?.date).toLocaleDateString('es-ES') : ''}
                                readOnly
                                className="font-medium"
                            />
                        </div>
                    </div>
                </FormGroup>
            </div>
        </div>
    );
}